import React from "react";

import { AddRounded, CloseRounded, InfoRounded, SaveRounded } from "@mui/icons-material";
import { Box, DialogContent, Divider, IconButton } from "@mui/material";
import { httpsCallable } from "firebase/functions";

import useFirebase from "../../../../config/context/firebaseContext";
import useModal from "../../../../config/context/modalContext";
import { Dev } from "../../../../config/util/consoleUtil";

import SwitchMain from "../../../../components/InputMain/SwitchMain";
import { WideDialog } from "../../../../components/MainModalSimple/styles";
import MainSolidButton from "../../../../components/MainSolidButton";
import { ModalTransition } from "../../../../components/ModalTransition";
import PageHeader from "../../../../components/PageHeader";

import MainBox from "../../../../components/MainBox";
import MainText from "../../../../components/MainText";
import { BannerItemType, ModalBaseType } from "../../../../types/common";
import { UserInfoType } from "../../../../types/database";
import SearchUserField from "../../../Admin/AdminUserPage/components/SearchUserField";
import { UserModalContentDetails } from "../../../Admin/UserPage";

type ModalWriteBannerProps = ModalBaseType & {
  banner?: BannerItemType;
};

export default function ModalCreateChat({ onClose, banner }: ModalWriteBannerProps) {
  const { pushAlert } = useModal();
  const { functions } = useFirebase();

  const [open, setOpen] = React.useState(true);

  const [loading, setLoading] = React.useState(false);

  const [selectedUsers, setSelectedUsers] = React.useState<UserInfoType[]>([]);

  const [enabled, setEnabled] = React.useState(false);
  const [visible, setVisible] = React.useState(false);
  const [isStaff, setIsIstaff] = React.useState(false);

  const handleCreateChat = async () => {
    try {
      setLoading(true);
      const createNewChat = httpsCallable(functions, "createNewChat");

      const payload = {
        visible,
        enabled,
        isStaff,
        usersIds: selectedUsers.map((user) => user.id),
      } as any;

      const response = await createNewChat(payload);

      pushAlert({
        type: "success",
        message: (response?.data as any)?.message || "Chat criado",
      });

      handleClose(true);
    } catch (error) {
      console.log(...new Dev("handleCreateChat", error).Error());
      pushAlert({
        type: "error",
        message: `Erro ao salvar dados${
          (error as any).message ? ". " + (error as any).message : ""
        }`,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = (reload: boolean = false) => {
    setOpen(false);
    setTimeout(() => {
      if (onClose) onClose(reload);
    }, 200);
  };

  return (
    <WideDialog
      fullWidth={true}
      open={open}
      onClose={() => handleClose()}
      TransitionComponent={ModalTransition}
    >
      <DialogContent>
        <PageHeader
          size="subtitle"
          title={"Criar Chat"}
          rightIcon={<CloseRounded />}
          onRightAction={() => handleClose()}
        />

        <SearchUserField onUserClick={(user) => setSelectedUsers([...selectedUsers, user])} />

        {selectedUsers.map((user) => (
          <MainBox sx={{ display: "flex", justifyContent: "space-between" }}>
            <Box flex={1}>
              <MainText size="body" color="black">
                {user.name}
              </MainText>
              <MainText size="small" color="regular">
                {user.email}
              </MainText>
            </Box>
            <IconButton
              onClick={() => {
                pushAlert({
                  title: "Detalhes",
                  isClosable: true,
                  children: <UserModalContentDetails user={user} />,
                });
              }}
            >
              <InfoRounded />
            </IconButton>
          </MainBox>
        ))}

        {selectedUsers.length > 2 && (
          <Box mb={2}>
            <MainText size="caption" color="danger">
              No momento o sistema só consegue lidar com Chats entre 2 usuários
            </MainText>
          </Box>
        )}

        {selectedUsers.length === 0 ? (
          <Box mb={2}>
            <MainText size="caption" color="regular">
              Nenhum usuário adicionado...
            </MainText>
          </Box>
        ) : (
          <Divider sx={{ mb: 3, mt: 3 }} />
        )}

        <Box mb={2}>
          <SwitchMain
            name="Conversas Habilitadas"
            checked={enabled}
            noBottom
            onChange={setEnabled}
          />
        </Box>

        <Box mb={2}>
          <SwitchMain name="Chat Visível" checked={visible} noBottom onChange={setVisible} />
        </Box>

        <Box mb={2}>
          <SwitchMain name="Chat Staff" checked={isStaff} noBottom onChange={setIsIstaff} />
        </Box>

        <Box my={2}>
          <MainSolidButton
            disabled={selectedUsers.length === 0}
            loading={loading}
            text={banner ? "Salvar" : "Criar"}
            icon={banner ? <SaveRounded /> : <AddRounded />}
            onClick={() => handleCreateChat()}
          />
        </Box>
      </DialogContent>
    </WideDialog>
  );
}
